import { useState, useEffect } from "react";
import axios from "axios";
import { Target, FunnelSimple, ChartBar, Scales, Lightbulb } from "@phosphor-icons/react";
import { Tip } from "./commerceUi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Insufficient = ({ d }) => (
  <div className="rounded-xl border border-dashed border-zinc-200 bg-white/60 p-5 text-center" data-testid="pv-insufficient">
    <p className="text-sm text-zinc-500">Not enough validated outcomes yet.</p>
    {d?.validated_outcomes != null && (
      <p className="mt-1 text-[11px] text-zinc-400">{d.validated_outcomes} / {d.needed} needed. Metrics populate from real outcomes as the pipeline runs — never estimated.</p>
    )}
  </div>
);

const Section = ({ icon: Icon, title, tip, children, testid }) => (
  <div data-testid={testid}>
    <div className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-3 flex items-center gap-1.5">
      <Icon size={13} weight="fill" /> {title}{tip && <Tip text={tip} />}
    </div>
    {children}
  </div>
);

const Bar = ({ label, value, max, right }) => (
  <div className="flex items-center gap-3">
    <span className="text-[11px] w-40 text-zinc-600 flex-shrink-0">{label}</span>
    <div className="flex-1 h-5 rounded bg-zinc-100 overflow-hidden">
      <div className="h-full bg-violet-500/80 rounded transition-all flex items-center justify-end pr-2"
        style={{ width: `${max ? (value / max) * 100 : 0}%`, minWidth: value ? "28px" : 0 }}>
        <span className="text-[10px] font-bold text-white">{value}</span>
      </div>
    </div>
    <span className="text-[11px] w-24 text-right text-zinc-400">{right}</span>
  </div>
);

const PredictionValidation = () => {
  const [d, setD] = useState(null);

  useEffect(() => {
    axios.get(`${API}/commerce/validation/dashboard`).then(({ data }) => setD(data)).catch(() => setD(false));
  }, []);

  if (d === null) return <div className="text-center py-16 text-zinc-400" data-testid="pv-loading">Loading prediction validation…</div>;
  if (d === false) return <div className="text-center py-16 text-rose-400" data-testid="pv-error">Couldn't load validation dashboard.</div>;

  const acc = d.engine_accuracy;
  const fn = d.funnel.funnel;
  const cal = d.calibration;
  const hva = d.human_vs_ai;
  const learn = d.learning;
  const maxStage = Math.max(...fn.map((r) => r.remaining), 1);

  const CalChart = ({ title, data }) => (
    <div className="rounded-2xl bg-white/85 border border-violet-100 p-4">
      <div className="text-[11px] font-bold text-zinc-700 mb-2">{title} → Winner rate</div>
      {data.insufficient_data ? <Insufficient d={data} /> : (
        <div className="space-y-1.5">
          {data.buckets.map((b) => (
            <Bar key={b.bucket} label={b.bucket} value={b.winner_pct ?? 0} max={100}
              right={b.winner_pct == null ? `n=${b.n} (low)` : `${b.n} samples`} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-7" data-testid="commerce-prediction-validation">
      <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-white border border-violet-100 p-4 text-[11px] text-zinc-500" data-testid="pv-totals">
        {d.totals.opportunities} opportunities · {d.totals.outcomes_recorded} outcomes recorded · {d.totals.validated_outcomes} validated · {d.totals.founder_signals} founder signals. All numbers derive from real outcomes — nothing is estimated.
      </div>

      <Section icon={Target} title="Engine Accuracy" testid="pv-accuracy"
        tip="How well the engine's scores predicted real winners. Populates from validated outcomes only.">
        {acc.insufficient_data ? <Insufficient d={acc} /> : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-2xl bg-white/85 border border-violet-100 p-5"><div className="text-[10px] uppercase text-zinc-400">Avg Winner Confidence</div><div className="text-3xl font-black text-emerald-600 mt-2">{acc.avg_winner_decision_confidence ?? "—"}</div></div>
            <div className="rounded-2xl bg-white/85 border border-violet-100 p-5"><div className="text-[10px] uppercase text-zinc-400">Avg Winner Evidence</div><div className="text-3xl font-black text-emerald-600 mt-2">{acc.avg_winner_evidence_strength ?? "—"}</div></div>
            <div className="rounded-2xl bg-white/85 border border-violet-100 p-5"><div className="text-[10px] uppercase text-zinc-400">Validated Outcomes</div><div className="text-3xl font-black text-zinc-900 mt-2">{acc.validated_outcomes}</div></div>
          </div>
        )}
      </Section>

      <Section icon={FunnelSimple} title="Prediction Funnel" testid="pv-funnel"
        tip="Opportunities surviving each stage from discovery to Winner, with avg confidence & evidence.">
        <div className="rounded-2xl bg-white/85 border border-violet-100 p-4 space-y-1.5">
          {fn.map((r) => (
            <Bar key={r.stage} label={r.stage} value={r.remaining} max={maxStage}
              right={r.avg_decision_confidence != null ? `DC ${r.avg_decision_confidence} · ES ${r.avg_evidence_strength ?? "—"}` : (r.dropoff_pct_from_prev ? `−${r.dropoff_pct_from_prev}%` : "")} />
          ))}
        </div>
      </Section>

      <Section icon={ChartBar} title="Calibration — do the scores mean anything?" testid="pv-calibration"
        tip="For each score bucket, the % that became real Winners. Rising left-to-right = well-calibrated.">
        <div className="grid lg:grid-cols-2 gap-3">
          <CalChart title="Decision Confidence" data={cal.decision_confidence} />
          <CalChart title="Evidence Strength" data={cal.evidence_strength} />
        </div>
      </Section>

      <Section icon={Scales} title="Human vs AI Accuracy" testid="pv-human-vs-ai"
        tip="Does the AI agree with humans — and were humans actually right (revenue, winners)?">
        {hva.insufficient_data && hva.funnel.every((f) => f.count === 0)
          ? <Insufficient d={hva} />
          : <div className="rounded-2xl bg-white/85 border border-violet-100 p-4 space-y-1.5">
              {hva.funnel.map((f) => <Bar key={f.stage} label={f.stage} value={f.count} max={Math.max(...hva.funnel.map((x) => x.count), 1)} right="" />)}
              {hva.insufficient_data && <p className="text-[11px] text-zinc-400 mt-2">Revenue/Winner stages populate once outcomes are validated.</p>}
            </div>}
      </Section>

      <Section icon={Lightbulb} title="Learning" testid="pv-learning"
        tip="Why winners won, why failures failed, and which scores were over/under-rated.">
        {learn.insufficient_data ? <Insufficient d={learn} /> : (
          <div className="grid lg:grid-cols-2 gap-3 text-[11px]">
            <div className="rounded-2xl bg-white/85 border border-violet-100 p-4">
              <div className="font-bold text-emerald-700 mb-1">Top winner reasons</div>
              {learn.top_winner_reasons.map((r, i) => <div key={i} className="text-zinc-600">• {r.reason} ({r.count})</div>)}
              <div className="font-bold text-emerald-700 mt-3 mb-1">Top markets</div>
              {learn.top_markets.map((m) => <span key={m.market} className="inline-block mr-2 text-zinc-600">{m.market} {m.winner_pct}%</span>)}
            </div>
            <div className="rounded-2xl bg-white/85 border border-violet-100 p-4">
              <div className="font-bold text-rose-600 mb-1">Top failure reasons</div>
              {learn.top_failure_reasons.map((r, i) => <div key={i} className="text-zinc-600">• {r.reason} ({r.count})</div>)}
              <div className="font-bold text-rose-600 mt-3 mb-1">Most overrated / underrated</div>
              <div className="text-zinc-600">Overrated: {learn.most_overrated.map((x) => x.cluster_id).join(", ") || "—"}</div>
              <div className="text-zinc-600">Underrated: {learn.most_underrated.map((x) => x.cluster_id).join(", ") || "—"}</div>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
};

export default PredictionValidation;
